import React, { useState, createContext, useContext, ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchOutSideURLLists } from "../redux/slice/codetableSlice";
import { useAppDispatch } from "../redux/slice/redux-hooks";

type NavigationContextType = {
  authorizeNavigation: (path: string) => void;
  isAuthorizedNavigation: (path: string) => boolean;
  navigate: (path: string | number , state?: object) => void;
  authorizedPaths: string[];
};

const NavigationContext = createContext<NavigationContextType>({
  authorizeNavigation: (path: string) => { },
  isAuthorizedNavigation: (path: string) => false,
  navigate: (path: string | number , state?: object) => { },
  authorizedPaths: []
});

/** Routes that may be opened directly in a new tab (e.g. window.open) without in-app navigation. */
const DIRECT_ACCESS_ROUTE_PREFIXES = [
  "details",
  "outside_platform_order_details",
  "migrate_details",
  "finance_hq_sales_sheet",
  "edit_invoice",
  "billing_detail",
  "migrate_billing_detail",
  "activity_center",
  "commission_details",
];

export const NavigationContextProvider = ({ children }: { children: ReactNode }) => {

  const [authorizedPaths, setAuthorizedPaths] = useState<string[]>(['/', '/signIn', '/create_account_step']); // CS-1865
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();


  const authorizeNavigation = (path: string) => {
    setAuthorizedPaths(prev => [...prev, path]);
  };

  const isAuthorizedNavigation = (path: string) => {
    return authorizedPaths.includes(path);
  };

  const isNotAllowURLs = async (path: string) => {
    const pathParts = path.replace(/^\/+/, '').split('/');
    if (DIRECT_ACCESS_ROUTE_PREFIXES.includes(pathParts[0])) {
      return false;
    }

    let allowURLs = await fetchAllowUrls();
    for (let i = 0; i < allowURLs?.length; i++) {
      const urlParts = allowURLs[i].split('/');
      for (let i = 0; i < urlParts.length; i++) {
        if (urlParts[i] === pathParts[i]) 
          return false;
      }
    }
    return true;
  };

  const fetchAllowUrls = async () => {
    let urlsResponse = await dispatch(fetchOutSideURLLists({ skipErrorPopup: true }));
    return urlsResponse?.payload?.data;
  }

  const navigateTo = (path: string | number , state?: object) => {
    if (typeof path === 'number')
      navigate(path);
    else {
      authorizeNavigation(path);
      navigate(path, state);
    }
  };

  useEffect(()=>{
    checkconditions();
  },[authorizedPaths]);

  const checkconditions = async () => {
    if (location.pathname !== '/error' && location.pathname !== '/restricted' && !isAuthorizedNavigation(location.pathname) && await isNotAllowURLs(location.pathname)) {
      if (location.key === "default") {
        navigate('/restricted');
      } 
    } 
  }

  return (
    <>
      <NavigationContext.Provider
        value={{
          authorizeNavigation,
          isAuthorizedNavigation,
          navigate: navigateTo,
          authorizedPaths
        }}
      >
        {children}
      </NavigationContext.Provider>
    </>
  )
};

export const useNavigation = () => useContext(NavigationContext);
